import React, { useEffect, useState, useRef } from "react";
import { TVehicle } from "models/inventory/interfaces/vehicle.interface";
import { Typography, Button } from "design_system/src";
import { Grid } from "@mui/material";
import { scrollToSectionEnd, scrollToSectionWithOffset } from "views/VehicleDetail/helpers/scrolling";
import { ReactComponent as StarFilled } from "design_system/src/static/icons/star-filled.svg";
import { VehicleReviewSummary } from "models/inventory/interfaces/vehicle-review-summary.interfaces";
import ReviewCard from "./ReviewCard";
import ReviewScore from "./ReviewScore";
import ReviewBar from "./ReviewBar";

interface ReviewsProps {
  vehicle?: TVehicle;
  vehicleReview?: VehicleReviewSummary;
}

const Reviews = ({ vehicle, vehicleReview }: ReviewsProps) => {
  const [showMore, setShowMore] = useState<boolean>(false);
  const isFirstRender = useRef(true);

  const showMoreReviews = () => {
    setShowMore(!showMore);
  };

  const getBarRatingValue = (barValue: number): number => {
    if (vehicleReview && vehicleReview.count > 0) {
      let sum = 0;
      if (barValue === 5) {
        sum = vehicleReview.data.filter((review) => review.getGlobalRating() === barValue).length;
      } else if (barValue === 1) {
        sum = vehicleReview.data.filter((review) => review.getGlobalRating() < barValue + 1).length;
      } else {
        sum = vehicleReview.data.filter((review) => review.getGlobalRating() >= barValue && review.getGlobalRating() < barValue + 1).length;
      }

      return sum / vehicleReview.count * 100;
    }

    return 0;
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!showMore) {
      scrollToSectionWithOffset("reviews-section");
    } else {
      scrollToSectionEnd("reviews-section");
    }
  }, [showMore]);

  if (!vehicleReview || vehicleReview?.count === 0) return null;

  return (
    <section id="reviews-section">
      <div>
        <Typography
          scale="large"
          weight="600"
          className="m_b_sm"
        >
          Calificación de la moto {vehicle?.brand.name ?? ""} {vehicle?.model.name ?? ""} {vehicle?.cylindersCapacity ?? ""}
        </Typography>
        <Grid
          container
          justifyContent="center"
          columnSpacing={{ xs: 0, sm: 2, md: 3, lg: 3, xl: 3 }}
          rowSpacing={2}
          className="m_b_xl"
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={5}
          >
            <div className="w_80_per_desktop">
              <div className="display_flex flex_gap_lg m_b_md">
                <Typography scale="heading2" weight="600" className="text_primary_300">
                  {vehicleReview.getGlobalRating().toFixed(1)}
                </Typography>
                <div>
                  <ReviewScore value={vehicleReview.getGlobalRating()} />
                  <Typography scale="medium" weight="400" className="text_neutral_700">
                    {vehicleReview.count} {vehicleReview.count === 1 ? "calificación" : "calificaciones" }
                  </Typography>
                </div>
              </div>

              {vehicleReview && vehicleReview.data.length > 0 && <div className="m_b_md">
                <div className="display_flex m_b_sm flex_justify_end flex_align_center flex_gap_sm">
                  <ReviewBar value={getBarRatingValue(5)}/>
                  <Typography scale="medium" weight="400" className="text_neutral_700">
                    5
                  </Typography>
                  <StarFilled className="text_primary_300" />
                </div>
                <div className="display_flex m_b_sm flex_justify_end flex_align_center flex_gap_sm">
                  <ReviewBar value={getBarRatingValue(4)}/>
                  <Typography scale="medium" weight="400" className="text_neutral_700">
                    4
                  </Typography>
                  <StarFilled className="text_primary_300" />
                </div>
                <div className="display_flex m_b_sm flex_justify_end flex_align_center flex_gap_sm">
                  <ReviewBar value={getBarRatingValue(3)}/>
                  <Typography scale="medium" weight="400" className="text_neutral_700">
                    3
                  </Typography>
                  <StarFilled className="text_primary_300" />
                </div>
                <div className="display_flex m_b_sm flex_justify_end flex_align_center flex_gap_sm">
                  <ReviewBar value={getBarRatingValue(2)}/>
                  <Typography scale="medium" weight="400" className="text_neutral_700">
                    2
                  </Typography>
                  <StarFilled className="text_primary_300" />
                </div>
                <div className="display_flex m_b_sm flex_justify_end flex_align_center flex_gap_sm">
                  <ReviewBar value={getBarRatingValue(1)}/>
                  <Typography scale="medium" weight="400" className="text_neutral_700">
                    1
                  </Typography>
                  <StarFilled className="text_primary_300" />
                </div>
              </div>}

              <div className="m_b_md">
                <Typography scale="medium" weight="600">
                  Calificación por características
                </Typography>
                <Typography scale="small" weight="400" className="text_neutral_700">
                  Promedio de calificaciones por característica, basado en opiniones reales.
                </Typography>
              </div>

              <div className="m_b_md">
                <div className="m_b_sm">
                  <Typography scale="medium" weight="400">
                    Rendimiento
                  </Typography>
                  <ReviewScore value={vehicleReview.getPerformanceRating()} />
                </div>
                <div className="m_b_sm">
                  <Typography scale="medium" weight="400">
                    Potencia
                  </Typography>
                  <ReviewScore value={vehicleReview.getPowerRating()} />
                </div>
                <div className="m_b_sm">
                  <Typography scale="medium" weight="400">
                    Estabilidad
                  </Typography>
                  <ReviewScore value={vehicleReview.getStabilityRating()} />
                </div>
                <div className="m_b_sm">
                  <Typography scale="medium" weight="400">
                    Diseño
                  </Typography>
                  <ReviewScore value={vehicleReview.getDesignRating()} />
                </div>
              </div>
            </div>
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={7}
          >
            {showMore && vehicleReview.data.map((data, index) => {
              return <ReviewCard data={data} key={index} />;
            })}
            {!showMore && vehicleReview.data.slice(0, 2).map((data, index) => {
              return <ReviewCard data={data} key={index} />;
            })}

            {vehicleReview.count > 2 && <Button variant="link" scale="small" onClick={showMoreReviews}>
              <Typography scale="small" weight="600" className="text_secondary_500 text_decoration_underline">
                {showMore ? "Mostrar menos reseñas" : "Mostrar todas las reseñas"}
              </Typography>
            </Button>}
          </Grid>
        </Grid>
      </div>
    </section>
  );
};

export default Reviews;
